import { Form, Modal, Switch, Table } from "antd"
import { Cards } from "../../../components/cards/frame/cards-frame"
import { InfoCircleOutlined } from '@ant-design/icons';
import { UserTableStyleWrapper } from "../style";
import { TableWrapper } from "../../styled";
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../../components/buttons/buttons";
import { useParams } from "react-router-dom";
import HttpStatus from "../../../utility/status.enum";
import { useDispatch } from "react-redux";
import { getConfigurationDetails, updateConfiguration } from "../../../redux/configurations/actionCreator";
import Spinner from "../../common/Spinner";
import Functions from '../../../utility/functions';
import Notification from '../../../utility/notification.js';

const Configurations = () => {
    const { perPageRecord } = global;
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const hostSlug = useParams()?.hostSlug ?? '';
    const [configurationList, setConfigurationList] = useState([]);
    const [spinnerLoader, setSpinnerLoader] = useState(false);
    const buttonName = 'Save';
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: perPageRecord,
        totalItems: 0,
    });
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState('');

    const handleToggleChange = (checkedValue, record, field) => {

        //  Helper to convert field key → readable channel name
        const getChannelName = (field) => {
            if (field === 'is_airbnb_enable') return 'Airbnb';
            if (field === 'is_vrbo_enable') return 'Vrbo';
            if (field === 'is_booking_com_enable') return 'Booking.com';
            if (field === 'is_other_enable') return 'Other';
            return 'Channel';
        };

        const channelName = getChannelName(field);

        //  Get dependent rows for validation rules
        const idUploadRow = configurationList.find(
            item => item.feature_slug === 'id_upload'
        );

        const idVerificationRow = configurationList.find(
            item => item.feature_slug === 'veriff_verification'
        );

        const offenderRow = configurationList.find(
            item => item.feature_slug === 'sex_offender'
        );

        //  Rule 1: Prevent both ID Upload & ID Verification from being ON together
        if (checkedValue) {
            if (record.feature_slug === 'id_upload' && idVerificationRow?.[field] === 1) {
                Notification.addNotificationError(
                    `ID Verification is already ON for ${channelName}. Only one can be enabled`
                );
                return;
            }

            if (record.feature_slug === 'veriff_verification' && idUploadRow?.[field] === 1) {
                Notification.addNotificationError(
                    `ID Upload is already ON for ${channelName}. Only one can be enabled`
                );
                return;
            }
        }

        //  Rule 2: Offender Check can be enabled ONLY if ID Verification is ON
        if (record.feature_slug === 'sex_offender' && checkedValue) {
            if (idVerificationRow?.[field] !== 1) {
                Notification.addNotificationError(
                    `To enable Offender Check, please turn ON ID Verification for ${channelName}`
                );
                return;
            }
        }

        //  Rule 3: Cannot turn OFF ID Verification if Offender Check is ON
        if (record.feature_slug === 'veriff_verification' && !checkedValue) {
            if (offenderRow?.[field] === 1) {
                Notification.addNotificationError(
                    `Cannot turn OFF ID Verification because Offender Check is ON for ${channelName}`
                );
                return;
            }
        }

        //  Apply the toggle change to current row & field
        const updatedList = configurationList.map((item) => {
            if (item.feature_slug === record.feature_slug) {
                return {
                    ...item,
                    [field]: checkedValue ? 1 : 2
                };
            }
            return item;
        });
        setConfigurationList(updatedList);
    };

    // Master toggle: Controls all channel toggles (Airbnb, Vrbo, Booking.com, Others)
    // Also validates business rules before applying changes to all channels
    const handleAllToggleChange = (checked, record) => {

        //  Helper for readable channel names
        const getChannelName = (field) => {
            if (field === 'is_airbnb_enable') return 'Airbnb';
            if (field === 'is_vrbo_enable') return 'Vrbo';
            if (field === 'is_booking_com_enable') return 'Booking.com';
            if (field === 'is_other_enable') return 'Other';
            return 'Channel';
        };

        //  Fetch related rows for validations
        const idUploadRow = configurationList.find(
            item => item.feature_slug === 'id_upload'
        );

        const idVerificationRow = configurationList.find(
            item => item.feature_slug === 'veriff_verification'
        );

        const offenderRow = configurationList.find(
            item => item.feature_slug === 'sex_offender'
        );

        //  All channel fields
        const fields = [
            'is_airbnb_enable',
            'is_vrbo_enable',
            'is_booking_com_enable',
            'is_other_enable'
        ];

        //  Rule 1: Prevent ID Upload & ID Verification conflict across ANY channel
        if (checked) {
            if (record.feature_slug === 'id_upload') {
                const invalidChannels = fields.filter(
                    field => idVerificationRow?.[field] === 1
                ).map(getChannelName);

                if (invalidChannels.length) {
                    Notification.addNotificationError(
                        `ID Verification is already ON for ${invalidChannels.join(', ')}`
                    );
                    return;
                }
            }

            if (record.feature_slug === 'veriff_verification') {
                const invalidChannels = fields.filter(
                    field => idUploadRow?.[field] === 1
                ).map(getChannelName);

                if (invalidChannels.length) {
                    Notification.addNotificationError(
                        `ID Upload is already ON for ${invalidChannels.join(', ')}`
                    );
                    return;
                }
            }
        }

        //  Rule 2: Offender Check requires ID Verification ON for ALL channels
        if (record.feature_slug === 'sex_offender' && checked) {
            const invalidChannels = fields.filter(
                field => idVerificationRow?.[field] !== 1
            ).map(getChannelName);

            if (invalidChannels.length) {
                Notification.addNotificationError(
                    `To enable Offender Check, please turn ON ID Verification for ${invalidChannels.join(', ')}`
                );
                return;
            }
        }

        //  Rule 3: Cannot disable ID Verification if Offender is ON in ANY channel
        if (record.feature_slug === 'veriff_verification' && !checked) {
            const invalidChannels = fields.filter(
                field => offenderRow?.[field] === 1
            ).map(getChannelName);

            if (invalidChannels.length) {
                Notification.addNotificationError(
                    `Cannot turn OFF ID Verification because Offender Check is ON for ${invalidChannels.join(', ')}`
                );
                return;
            }
        }

        //  Apply toggle to ALL channels in that row
        const updatedList = configurationList.map((item) => {
            if (item.feature_slug === record.feature_slug) {
                return {
                    ...item,
                    is_airbnb_enable: checked ? 1 : 2,
                    is_vrbo_enable: checked ? 1 : 2,
                    is_booking_com_enable: checked ? 1 : 2,
                    is_other_enable: checked ? 1 : 2,
                };
            }
            return item;
        });

        setConfigurationList(updatedList);
    };

    //  Handles info icon click (Intercom OR Modal fallback)
    const handleInfoClick = (e, record) => {
        e?.preventDefault();

        const description = record?.description || '';

        //  Check if description contains Intercom article ID
        const hasIntercomArticleId = Functions.findIntercomArticleId(description);

        //  If Intercom ID exists → let Intercom handle it
        //  Else -> fallback to modal
        if (!hasIntercomArticleId) {
            setSelectedDescription(description);
            setIsInfoModalOpen(true);
        }
    };

    const cmsTableColumns = [
        {
            title: 'Feature',
            dataIndex: 'title',
            key: 'title',
            width: '50%',
            render: (text, record) => (
                <div className="feature-info-icon">
                    <span>{text}</span>
                    {/*  Show info icon ONLY if is_allowed_info === 1 */}
                    {record?.is_allowed_info === 1 && (
                        <span onClick={(e) => handleInfoClick(e, record)}>
                            <InfoCircleOutlined className='configuration-info-icon' />
                        </span>
                    )}
                </div>
            )
        },
        {
            title: 'All',
            dataIndex: 'all',
            key: 'all',
            width: '10%',
            render: (_, record) => {

                //  Collect all channel values
                const values = [
                    record.is_airbnb_enable,
                    record.is_vrbo_enable,
                    record.is_booking_com_enable,
                    record.is_other_enable
                ];

                //  Determine toggle state
                const allOn = values.every(v => v === 1);   // All enabled
                const allOff = values.every(v => v === 2);  // All disabled
                const isMixed = !allOn && !allOff;          // Partial state

                return (
                    <div className="switch-center">
                        <Switch
                            size="large"
                            checked={allOn}
                            onChange={(checked) =>
                                handleAllToggleChange(checked, record)
                            }
                            className={`switch-toggle  ${isMixed ? 'switch-middle' : ''}`}
                        />
                    </div>
                );
            }
        },
        {
            title: 'Airbnb',
            dataIndex: 'is_airbnb_enable',
            key: 'is_airbnb_enable',
            width: '10%',
            render: (text, record) => (
                <div className="switch-center">
                    <Switch
                        onChange={(checked) =>
                            handleToggleChange(checked, record, 'is_airbnb_enable')
                        }
                        size="large"
                        checked={text === 1}
                        className='switch-toggle' />
                </div>
            )
        },
        {
            title: 'Vrbo',
            dataIndex: 'is_vrbo_enable',
            key: 'is_vrbo_enable',
            width: '10%',
            render: (text, record) => (
                <div className="switch-center">
                    <Switch
                        onChange={(checked) =>
                            handleToggleChange(checked, record, 'is_vrbo_enable')
                        }
                        checked={record.is_vrbo_enable === 1}
                        size="large" className='switch-toggle' />
                </div>
            )
        },
        {
            title: 'Booking.com',
            dataIndex: 'is_booking_com_enable',
            key: 'is_booking_com_enable',
            width: '10%',
            render: (text, record) => (
                <div className="switch-center">
                    <Switch
                        onChange={(checked) =>
                            handleToggleChange(checked, record, 'is_booking_com_enable')
                        }
                        size="large"
                        checked={record.is_booking_com_enable === 1}
                        className='switch-toggle' />
                </div>
            )
        },
        {
            title: 'Others',
            dataIndex: 'is_other_enable',
            key: 'is_other_enable',
            width: '10%',
            render: (text, record) => (
                <div className="switch-center">
                    <Switch
                        onChange={(checked) =>
                            handleToggleChange(checked, record, 'is_other_enable')
                        }
                        size="large"
                        checked={record.is_other_enable === 1}
                        className='switch-toggle' />
                </div>
            )
        },
    ].filter(Boolean);

    const handlePaginationChange = (page, pageSize) => {
        setPagination({
            ...pagination,
            currentPage: page,
            pageSize,
        });
    };
    const handlePageSizeChange = (current, size) => {
        setPagination(({
            ...pagination,
            currentPage: current,
            pageSize: size,
        }));
    }

    //  Fetch configuration list from API
    const fetchConfigurationdata = async () => {
        try {
            setSpinnerLoader(true);
            const parms = hostSlug ? '?host_slug=' + hostSlug : '';
            const response = await dispatch(
                getConfigurationDetails(parms, (response) => {
                    return response;
                }),
            );
            //  Handle API error
            if (response?.status !== HttpStatus.HTTP_OK) {
                Notification.addNotificationError(response.message);
            }
            //  Set data into state
            else {
                setConfigurationList(response?.result)
            }
        } catch (error) {
        } finally {
            setSpinnerLoader(false);
        }
    };

    const handleFormSubmit = useCallback(
        async () => {
            //  Prepare payload
            let requestData = {
                host_slug: hostSlug,
                configurations: configurationList.map((item) => ({
                    feature_slug: item.feature_slug,
                    is_airbnb_enable: item.is_airbnb_enable,
                    is_vrbo_enable: item.is_vrbo_enable,
                    is_booking_com_enable: item.is_booking_com_enable,
                    is_other_enable: item.is_other_enable
                }))
            };

            //  Submit updated configuration to backend
            const updateConfigurations = async () => {
                try {
                    setSpinnerLoader(true);
                    const updateConfigurationResponse = await dispatch(
                        updateConfiguration(requestData, (response) => {
                            return response;
                        }),
                    );
                    if (updateConfigurationResponse?.status !== HttpStatus.HTTP_OK) {
                        Notification.addNotificationError(updateConfigurationResponse.message);
                    } else {
                        Notification.addNotificationSuccess(updateConfigurationResponse.message);
                        fetchConfigurationdata();
                    }
                } catch (error) {
                } finally {
                    setSpinnerLoader(false);
                }
            }
            updateConfigurations();
        },
        [dispatch, configurationList],
    );

    useEffect(() => {
        fetchConfigurationdata();
    }, [hostSlug, pagination.currentPage, pagination.pageSize]);

    return (
        <>
            <Form form={form} name="Form" onFinish={handleFormSubmit} >
                <Spinner loader={spinnerLoader} />
                <Cards xs={24} headless className="borderRadius common_fiter_table">
                    <UserTableStyleWrapper>
                        <div className="contact-table">
                            <TableWrapper className="table-responsive">
                                <div className="configuration-table">
                                <Table
                                    dataSource={configurationList}
                                    columns={cmsTableColumns}
                                    rowKey="slug"
                                    pagination={{
                                        current: pagination.currentPage,
                                        pageSize: pagination.pageSize,
                                        total: pagination.totalItems,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                        onChange: handlePaginationChange,
                                        onShowSizeChange: handlePageSizeChange
                                    }}
                                />
                                </div>
                            </TableWrapper>
                        </div>
                    </UserTableStyleWrapper>
                </Cards>
            </Form>
            <Button
                onClick={() => form.submit()}
                htmlType="submit"
                type="primary"
                size="default"
                shape="circle"
                className="export-save-button w-20"
            >
                {spinnerLoader ? 'Loading...' : buttonName}
            </Button>

            <Modal
                open={isInfoModalOpen}
                onCancel={() => setIsInfoModalOpen(false)}
                footer={null}
                className="modal-md"
                destroyOnClose
            >
                <div className="Idmodalbox earlyCheckinPopup">
                    <div className="hostPayout">
                        <div
                            className="modal-content"
                            dangerouslySetInnerHTML={{ __html: selectedDescription }}
                        />
                    </div>
                </div>
            </Modal>
        </>
    )

}
export default Configurations